import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Appointment, Employee, WorkHours, BitStringData } from '../../types';
import { dayjs, Dayjs } from '../../utils/dayjs-config';
import { extractWorkHours } from '../../time/timeUtils';
import { useAdminData } from '../../contexts/AdminDataContext';
import { useLocation, useNavigate } from 'react-router-dom';

export type ScheduleViewMode = 'perEmployee' | 'team';

export interface ScheduleHubPrefetchedData {
  employeeQuery: UseQueryResult<Employee>;
  hydrateAppointmentsQuery: UseQueryResult<boolean>;
  scheduleQuery: UseQueryResult<Map<string, string>>;
  workHoursQuery: UseQueryResult<WorkHours>;
  appointmentIds: string[];
  myAppointments: Appointment[];
  refreshAppointmentInCache: (appointmentId: string) => Promise<Appointment | null>;
}

export interface ScheduleHubContextValue {
  sharedDate: Dayjs;
  onSharedDateChange: (next: Dayjs) => void;
  prefetchedData: ScheduleHubPrefetchedData;
  selectedDateAppointments: Appointment[];
  workHours: BitStringData | null;
  scheduleViewMode: ScheduleViewMode;
  onScheduleViewModeChange: (next: ScheduleViewMode) => void;
  selectedEmployeeId: string;
  onSelectedEmployeeIdChange: (next: string) => void;
}

export const ScheduleHubContext = createContext<ScheduleHubContextValue | null>(null);

export const useScheduleHubContext = () => {
  const context = useContext(ScheduleHubContext);
  if (!context) {
    throw new Error('ScheduleHubContext must be used within ScheduleHub');
  }
  return context;
};

export const useScheduleHubViewModel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sharedDate, setSharedDate] = useState<Dayjs>(dayjs.tz());
  const [selectedDateAppointments, setSelectedDateAppointments] = useState<Appointment[]>([]);
  const [workHours, setWorkHours] = useState<BitStringData | null>(null);
  const [scheduleViewMode, setScheduleViewMode] = useState<ScheduleViewMode>('perEmployee');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const {
    currentEmployeeQuery: employeeQuery,
    hydrateAppointmentsQuery,
    scheduleQuery,
    workHoursQuery,
    appointmentIds,
    myAppointments,
    refreshAppointmentInCache,
  } = useAdminData();
  const employeeId = employeeQuery.data?.id;

  const prefetchedData: ScheduleHubPrefetchedData = useMemo(
    () => ({
      employeeQuery,
      hydrateAppointmentsQuery,
      scheduleQuery,
      workHoursQuery,
      appointmentIds,
      myAppointments,
      refreshAppointmentInCache,
    }),
    [employeeQuery, hydrateAppointmentsQuery, scheduleQuery, workHoursQuery, appointmentIds, myAppointments, refreshAppointmentInCache]
  );

  const handleSharedDateChange = (next: Dayjs) => {
    setSharedDate(next);
  };

  const handleScheduleViewModeChange = (next: ScheduleViewMode) => {
    setScheduleViewMode(next);
    const params = new URLSearchParams(location.search);
    if (next === 'team') {
      params.set('view', 'team');
    } else {
      params.delete('view');
    }
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedView = params.get('view');
    if (requestedView === 'team') {
      setScheduleViewMode('team');
      return;
    }
    setScheduleViewMode('perEmployee');
  }, [location.search]);

  useEffect(() => {
    if (!employeeId) {
      return;
    }
    setSelectedEmployeeId((previous) => previous || employeeId);
  }, [employeeId]);

  useEffect(() => {
    if (!sharedDate) return;
    const dateKey = sharedDate.format('MMDDYYYY');
    const appointmentsForDay = myAppointments.filter((appointment) => appointment.appointmentDate === dateKey);
    setSelectedDateAppointments(appointmentsForDay);

    const bitString = prefetchedData.scheduleQuery.data?.get(dateKey) ?? null;
    if (bitString) {
      const hours = extractWorkHours(appointmentsForDay, bitString);
      setWorkHours(hours);
    } else {
      setWorkHours(null);
    }
  }, [sharedDate, myAppointments, prefetchedData.scheduleQuery.data]);

  return {
    sharedDate,
    onSharedDateChange: handleSharedDateChange,
    prefetchedData,
    selectedDateAppointments,
    workHours,
    scheduleViewMode,
    onScheduleViewModeChange: handleScheduleViewModeChange,
    selectedEmployeeId,
    onSelectedEmployeeIdChange: setSelectedEmployeeId,
  };
};
